/*
Authors: Hugo Lavergne, Niklaas Cotta
Last modified: 7/20/2023
Description: obtain paintings from backend

| type   | column          | description |
|--------|-----------------| ----------- |
| int    | `id`            |             |
| string | `description`   |             |
| string | `file`          |             |
| string | `file_type`     |             |
| int    | `player_id`     |             |
*/

import axiosInstance from 'utils/axiosInstance';
import { combineReducers } from 'redux';

import createRequestReducer, {
    dispatchNormalizedPromise,
    NormalizedActionMap,
    NormalizedSelector,
    Strategies,
} from 'reducers/createRequestReducer';
import paintingSchema from 'reducers/schemas/painting';

import { normalize } from 'normalizr';
import { objectToFormData } from 'utils/FormUtils';

import { map, values, sortBy, concat, filter } from 'lodash-es';



const PLAYER_PAINTING = 'PLAYER_PAINTING';
const PLAYER_PAINTING_SUCCESS = 'PLAYER_PAINTING_SUCCESS';
const PLAYER_PAINTING_ERROR = 'PLAYER_PAINTING_ERROR';

const CREATE_PLAYER_PAINTING = 'CREATE_PLAYER_PAINTING';
const CREATE_PLAYER_PAINTING_SUCCESS = 'CREATE_PLAYER_PAINTING_SUCCESS';
const CREATE_PLAYER_PAINTING_ERROR = 'CREATE_PLAYER_PAINTING_ERROR';

// const UPDATE_PLAYER_OBJECT_ASSET = 'UPDATE_PLAYER_OBJECT_ASSET';
// const UPDATE_PLAYER_OBJECT_ASSET_SUCCESS = 'UPDATE_PLAYER_OBJECT_ASSET_SUCCESS';
// const UPDATE_PLAYER_OBJECT_ASSET_ERROR = 'UPDATE_PLAYER_OBJECT_ASSET_ERROR';

const DELETE_PLAYER_OBJECT_ASSET = 'DELETE_PLAYER_OBJECT_ASSET';
const DELETE_PLAYER_OBJECT_ASSET_SUCCESS = 'DELETE_PLAYER_OBJECT_ASSET_SUCCESS';
const DELETE_PLAYER_OBJECT_ASSET_ERROR = 'DELETE_PLAYER_OBJECT_ASSET_ERROR';

const SORT_PLAYER_OBJECT_ASSET = 'SORT_PLAYER_OBJECT_ASSET';
const SORT_PLAYER_OBJECT_ASSET_SUCCESS = 'SORT_PLAYER_OBJECT_ASSET_SUCCESS';
const SORT_PLAYER_OBJECT_ASSET_ERROR = 'SORT_PLAYER_OBJECT_ASSET_ERROR';

let currentId = 0;

export const paintingSelector = {
    getPaintingState: (state) => state.assets.playerAssets,
    getAll: (state) => NormalizedSelector.getByIds(state.paintings.paintings, state.paintings.all),
};

export const fetchPlayerPainting = (playerId) => (dispatch) => {
    let operationId = currentId++;
    dispatch({ operationId, type: PLAYER_PAINTING });
    return axiosInstance
        .get(`/players/${playerId}/paintings`)
        .then(function (response) {
            dispatch({
                operationId,
                type: PLAYER_PAINTING_SUCCESS,
                normalized: normalize(response.data.data, [paintingSchema]),
            });
            return response;
        })
        .catch(function (error) {
            dispatch({ operationId, type: PLAYER_PAINTING_ERROR, error: error.toObject() });
            return Promise.reject(error);
        });
};

export const createPlayerPainting = (playerId, values) => (dispatch) => {
    dispatch({ type: CREATE_PLAYER_PAINTING });
    return axiosInstance
        .post(`/players/${playerId}/paintings`, objectToFormData(values))
        .then(function (response) {
            const normalized = normalize(response.data.data, paintingSchema);
            dispatch({
                type: CREATE_PLAYER_PAINTING_SUCCESS,
                normalized,
                id: normalized.result,
            });
            return response;
        })
        .catch(function (error) {
            dispatch({ type: CREATE_PLAYER_PAINTING_ERROR, error: error.toObject() });
            return Promise.reject(error);
        });
};

export const deletePlayerPainting = (paintingId) => (dispatch) => {
    dispatch({ type: DELETE_PLAYER_OBJECT_ASSET });
    return dispatchNormalizedPromise(
        axiosInstance.delete(`/paintings/${paintingId}`),
        null,
        [DELETE_PLAYER_OBJECT_ASSET_SUCCESS, DELETE_PLAYER_OBJECT_ASSET_ERROR],
        dispatch,
        { paintingId }
    );
};

// export const replacePlayerPainting = (playerId, unityId, assets) => (dispatch) => {
//     dispatch({ type: REPLACE_PLAYER_PAINTING });
//     return dispatchNormalizedPromise(
//         axiosInstance.put(`/players/${playerId}/unity_objects/${unityId}/assets`, assets),
//         [paintingSchema],
//         [REPLACE_PLAYER_PAINTING_SUCCESS, REPLACE_PLAYER_PAINTING_ERROR],
//         dispatch
//     );
// };
//
// export const sortPlayerObjectAsset = (playerId, unityId, { movedId, newPosition, newOrder }) => (dispatch) => {
//     dispatch({
//         type: SORT_PLAYER_OBJECT_ASSET,
//         data: {
//             newOrder,
//         },
//     });
//
//     return axiosInstance
//         .put(`/players/${playerId}/unity_objects/${unityId}/assets/${movedId}`, { position: newPosition })
//         .then(function (response) {
//             dispatch({
//                 type: SORT_PLAYER_OBJECT_ASSET_SUCCESS,
//             });
//             return response;
//         })
//         .catch(function (error) {
//             dispatch({ type: SORT_PLAYER_OBJECT_ASSET_ERROR, error: error.toObject() });
//             return Promise.reject(error);
//         });
// };
//
// export const deletePlayerObjectAsset = (playerId, unityId, id) => (dispatch) => {
//     dispatch({
//         type: DELETE_PLAYER_OBJECT_ASSET,
//         id,
//     });
//     return axiosInstance
//         .delete(`/players/${playerId}/unity_objects/${unityId}/assets/${id}`)
//         .then(function (response) {
//             dispatch({
//                 type: DELETE_PLAYER_OBJECT_ASSET_SUCCESS,
//                 id,
//             });
//             return response;
//         })
//         .catch(function (error) {
//             dispatch({ type: DELETE_PLAYER_OBJECT_ASSET_ERROR, id, error: error.toObject() });
//             return Promise.reject(error);
//         });
// };
//
// export const updatePlayerObjectAsset = (playerId, unityId, id, values) => (dispatch) => {
//     dispatch({
//         type: UPDATE_PLAYER_OBJECT_ASSET,
//         id,
//     });
//     return dispatchNormalizedPromise(
//         axiosInstance.put(`/players/${playerId}/unity_objects/${unityId}/assets/${id}`, values),
//         paintingSchema,
//         [UPDATE_PLAYER_OBJECT_ASSET_SUCCESS, UPDATE_PLAYER_OBJECT_ASSET_ERROR],
//         dispatch
//     );
// };

export default combineReducers({
    paintings: createRequestReducer([null, '*', null], {
        mapResponse: NormalizedActionMap.entities('paintings'),
        defaultStrategy: Strategies.merge,
    }),
    playerAssets: createRequestReducer(
        [
            PLAYER_PAINTING, 
            [
                PLAYER_PAINTING_SUCCESS, 
                { 
                    type: DELETE_PLAYER_OBJECT_ASSET_SUCCESS,
                    strategy: Strategies.remove,
                    mapResponse: (action) => action.paintingId,
                },
            ],
            PLAYER_PAINTING_ERROR
        ],
        { ignoreData: true, useOperationId: true }
    ),
    all: createRequestReducer([PLAYER_PAINTING, PLAYER_PAINTING_SUCCESS, PLAYER_PAINTING_ERROR], {
        mapResponse: NormalizedActionMap.result,
    }),
    playerAssetsSort: (
        state = {
            current: null,
            optimistic: null,
            isUpdating: false,
        },
        action
    ) => {
        const getOrdered = (entitiesByKey) => {
            return values(map(sortBy(values(entitiesByKey || {}), 'position'), 'id'));
        };

        switch (action.type) {
            case PLAYER_PAINTING: {
                return {
                    current: [],
                    optimistic: null,
                    isUpdating: false,
                };
            }
            case PLAYER_PAINTING_SUCCESS: {
                return {
                    current: getOrdered(action.normalized.entities.assets),
                    optimistic: null,
                    isUpdating: false,
                };
            }
            case CREATE_PLAYER_PAINTING_SUCCESS: {
                return {
                    current: concat(state.current, action.id),
                    optimistic: null,
                    isUpdating: false,
                };
            }
            // case REPLACE_PLAYER_PAINTING_SUCCESS: {
            //     return {
            //         current: action.normalized.result,
            //         optimistic: null,
            //         isUpdating: false,
            //     };
            // }
            case DELETE_PLAYER_OBJECT_ASSET: {
                return {
                    ...state,
                    isUpdating: true,
                    optimistic: filter(state.current, (value) => value !== action.id),
                };
            }
            case SORT_PLAYER_OBJECT_ASSET: {
                return {
                    ...state,
                    isUpdating: true,
                    optimistic: action.data.newOrder,
                };
            }
            //COMMIT
            case DELETE_PLAYER_OBJECT_ASSET_SUCCESS:
            case SORT_PLAYER_OBJECT_ASSET_SUCCESS: {
                return {
                    ...state,
                    current: state.optimistic || state.current,
                    isUpdating: false,
                    optimistic: null,
                };
            }
            //REVERT
            case DELETE_PLAYER_OBJECT_ASSET_ERROR:
            case SORT_PLAYER_OBJECT_ASSET_ERROR: {
                return {
                    ...state,
                    isUpdating: false,
                    optimistic: null,
                };
            }
        }

        return state;
    },
});
